import React, { createContext, useMemo } from 'react';
import { usePersistedReducer } from '../hooks/persist/usePersistedReducer';
import { Action, TChildren } from '../typings/common';
import { GenRequestType, StorageType } from '../utilities/constants/common';
import { extend } from '../utilities/helpers/common';

export interface ITextFullModel {
  text: string;
  id: string;
  genRequestType: GenRequestType;
}

interface Props {
  children: TChildren;
}

interface IGenerationState {
  generationText: string;
  generationId: string;
  generationChatId: string;
  generationTitle: string;
  generationSubjectGroup: string;
}

type IState = IGenerationState & {
  userHomeworkHelpRequest: string;
  userHomeworkHelpNextRequest: string;
  userHomeworkHelpSubject: string;
  userHomeworkHelpTextFromFiles: string[];
};

interface IContext {
  editorState: IState;
  editorDispatch: React.Dispatch<Action<ActionType>>;
}

const initialGenerationState: IGenerationState = {
  generationText: '',
  generationId: '',
  generationChatId: '',
  generationTitle: '',
  generationSubjectGroup: '',
};

const initialState: IState = {
  ...initialGenerationState,
  userHomeworkHelpRequest: '',
  userHomeworkHelpNextRequest: '',
  userHomeworkHelpSubject: '',
  userHomeworkHelpTextFromFiles: [],
};

enum ActionType {
  SET_GENERATION_TEXT = 'SET_GENERATION_TEXT',
  SET_GENERATION_ID = 'SET_GENERATION_ID',
  SET_GENERATION_CHAT_ID = 'SET_GENERATION_CHAT_ID',
  SET_GENERATION_TITLE = 'SET_GENERATION_TITLE',
  SET_GENERATION_SUBJECT_GROUP = 'SET_GENERATION_SUBJECT_GROUP',
  SET_DEFAULT_GENERATION_STATE = 'SET_DEFAULT_GENERATION_STATE',
  SET_USER_HOMEWORK_HELP_REQUEST = 'SET_USER_HOMEWORK_HELP_REQUEST',
  SET_USER_HOMEWORK_HELP_NEXT_REQUEST = 'SET_USER_HOMEWORK_HELP_NEXT_REQUEST',
  SET_USER_HOMEWORK_HELP_SUBJECT = 'SET_USER_HOMEWORK_HELP_SUBJECT',
  SET_USER_HOMEWORK_HELP_TEXT_FROM_FILES = 'SET_USER_HOMEWORK_HELP_TEXT_FROM_FILES',
  SET_USER_HOMEWORK_HELP_FILES = 'SET_USER_HOMEWORK_HELP_FILES',
}

export const ActionCreator = {
  setGenerationText: (generationText: string) => ({
    type: ActionType.SET_GENERATION_TEXT,
    payload: generationText,
  }),
  setGenerationId: (generationId: string) => ({
    type: ActionType.SET_GENERATION_ID,
    payload: generationId,
  }),
  setGenerationChatId: (generationChatId: string) => ({
    type: ActionType.SET_GENERATION_CHAT_ID,
    payload: generationChatId,
  }),
  setGenerationSubjectGroup: (generationSubjectGroup: string) => ({
    type: ActionType.SET_GENERATION_SUBJECT_GROUP,
    payload: generationSubjectGroup,
  }),
  setGenerationTitle: (generationTitle: string) => ({
    type: ActionType.SET_GENERATION_TITLE,
    payload: generationTitle,
  }),
  setDefaultGenerationState: () => ({
    type: ActionType.SET_DEFAULT_GENERATION_STATE,
    payload: initialGenerationState,
  }),
  setUserHomeworkHelpRequest: (request: string) => ({
    type: ActionType.SET_USER_HOMEWORK_HELP_REQUEST,
    payload: request,
  }),
  setUserHomeworkHelpNextRequest: (request: string) => ({
    type: ActionType.SET_USER_HOMEWORK_HELP_NEXT_REQUEST,
    payload: request,
  }),
  setUserHomeworkHelpSubject: (subject: string) => ({
    type: ActionType.SET_USER_HOMEWORK_HELP_SUBJECT,
    payload: subject,
  }),
  setUserHomeworkHelpTextFromFiles: (textFromFiles: string[]) => ({
    type: ActionType.SET_USER_HOMEWORK_HELP_TEXT_FROM_FILES,
    payload: textFromFiles,
  }),
};

const reducer = (state: IState, action: Action<ActionType>) => {
  switch (action.type) {
    case ActionType.SET_GENERATION_TEXT:
      return extend(state, {
        generationText: action.payload,
      });
    case ActionType.SET_GENERATION_ID:
      return extend(state, {
        generationId: action.payload,
      });
    case ActionType.SET_GENERATION_CHAT_ID:
      return extend(state, {
        generationChatId: action.payload,
      });
    case ActionType.SET_GENERATION_TITLE:
      return extend(state, {
        generationTitle: action.payload,
      });
    case ActionType.SET_GENERATION_SUBJECT_GROUP:
      return extend(state, {
        generationSubjectGroup: action.payload,
      });
    case ActionType.SET_DEFAULT_GENERATION_STATE:
      return extend(state, initialGenerationState);
    case ActionType.SET_USER_HOMEWORK_HELP_REQUEST:
      return extend(state, {
        userHomeworkHelpRequest: action.payload,
      });
    case ActionType.SET_USER_HOMEWORK_HELP_NEXT_REQUEST:
      return extend(state, {
        userHomeworkHelpNextRequest: action.payload,
      });
    case ActionType.SET_USER_HOMEWORK_HELP_SUBJECT:
      return extend(state, {
        userHomeworkHelpSubject: action.payload,
      });
    case ActionType.SET_USER_HOMEWORK_HELP_TEXT_FROM_FILES:
      return extend(state, {
        userHomeworkHelpTextFromFiles: action.payload,
      });
    default:
      return state;
  }
};

export const EditorContext = createContext<IContext>({
  editorState: initialState,
  editorDispatch: () => {},
});

const EditorProvider: React.FC<Props> = ({ children }) => {
  const [editorState, editorDispatch] = usePersistedReducer({
    reducer,
    initialState,
    storageType: StorageType.LOCAL,
  });
  const value = useMemo(
    () => ({ editorState, editorDispatch }),
    [editorState, editorDispatch]
  );

  return (
    <EditorContext.Provider value={value}>{children}</EditorContext.Provider>
  );
};

export default EditorProvider;
